/**
 * Blog Service
 * Handles communication with WordPress blog API
 */

import config from '../config/environment';

const WORDPRESS_API_BASE = `${config.apiEndpoint}/wp/v2`;


class BlogService {
  /**
   * Fetch all blog posts
   * @param {Object} options - Query options
   * @param {number} options.page - Page number
   * @param {number} options.per_page - Posts per page
   * @param {string} options.search - Search term
   * @param {number} options.category - Category ID
   * @returns {Promise<Array>} Array of blog posts
   */
  async getPosts(options = {}) {
    try {
      const params = new URLSearchParams({
        page: options.page || 1,
        per_page: options.per_page || 10,
        _embed: 'true', // Include featured images and author data
        ...options
      });

      // Remove undefined values
      Object.keys(params).forEach(key => {
        if (params.get(key) === 'undefined' || params.get(key) === '') {
          params.delete(key);
        }
      });

      const url = `${WORDPRESS_API_BASE}/posts?${params}`;

      const response = await fetch(url, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const posts = await response.json();

      return this.formatPosts(posts);
    } catch (error) {

      throw error;
    }
  }

  /**
   * Fetch a single blog post by ID
   * @param {number} id - Post ID
   * @returns {Promise<Object>} Blog post
   */
  async getPost(id) {
    try {
      const response = await fetch(`${WORDPRESS_API_BASE}/posts/${id}?_embed=true`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const post = await response.json();
      return this.formatPost(post);
    } catch (error) {

      throw error;
    }
  }

  /**
   * Fetch a single blog post by ID (alias for getPost)
   * @param {number} id - Post ID
   * @returns {Promise<Object>} Blog post
   */
  async getPostById(id) {
    if (!id && id !== 0) {
      throw new Error('Invalid post identifier');
    }

    const numericId = Number(id);

    if (!Number.isNaN(numericId) && `${numericId}` === `${id}`) {
      return this.getPost(numericId);
    }

    return this.getPostBySlug(id);
  }

  /**
   * Fetch a single blog post by slug
   * @param {string} slug - Post slug
   * @returns {Promise<Object>} Blog post
   */
  async getPostBySlug(slug) {
    if (!slug) {
      throw new Error('Invalid post slug');
    }

    try {
      const params = new URLSearchParams({
        slug,
        _embed: 'true'
      });

      const response = await fetch(`${WORDPRESS_API_BASE}/posts?${params.toString()}`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const posts = await response.json();
      const post = posts?.[0];

      if (!post) {
        throw new Error('Post not found');
      }

      return this.formatPost(post);
    } catch (error) {

      throw error;
    }
  }

  /**
   * Fetch full content for a post (for detail view)
   * @param {Object} post - Post object
   * @returns {Promise<Object>} Post with full content
   */
  async getPostWithFullContent(post) {
    try {
      // If we already have the full content, return it
      if (post.content && post.content.length > 200) {
        return post;
      }

      // Otherwise fetch the full post
      const fullPost = await this.getPost(post.id);
      return fullPost;
    } catch (error) {

      return post; // Return original post if fetch fails
    }
  }

  /**
   * Fetch blog categories
   * @returns {Promise<Array>} Array of categories
   */
  async getCategories() {
    try {
      const response = await fetch(`${WORDPRESS_API_BASE}/categories`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {

      throw error;
    }
  }

  /**
   * Fetch featured posts
   * @param {number} limit - Number of posts to fetch
   * @returns {Promise<Array>} Array of featured posts
   */
  async getFeaturedPosts(limit = 3) {
    try {
      const response = await fetch(`${WORDPRESS_API_BASE}/posts?_embed=true&per_page=${limit}&sticky=true`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const posts = await response.json();
      return this.formatPosts(posts);
    } catch (error) {

      throw error;
    }
  }

  /**
   * Search blog posts
   * @param {string} query - Search query
   * @param {Object} options - Additional search options
   * @returns {Promise<Array>} Array of matching posts
   */
  async searchPosts(query, options = {}) {
    try {
      const params = new URLSearchParams({
        search: query,
        _embed: 'true',
        ...options
      });

      const response = await fetch(`${WORDPRESS_API_BASE}/posts?${params}`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const posts = await response.json();
      return this.formatPosts(posts);
    } catch (error) {

      throw error;
    }
  }

  /**
   * Format posts for consistent structure
   * @param {Array} posts - Raw WordPress posts
   * @returns {Array} Formatted posts
   */
  formatPosts(posts) {
    return posts.map(post => this.formatPost(post));
  }

  /**
   * Format a single post for consistent structure
   * @param {Object} post - Raw WordPress post
   * @returns {Object} Formatted post
   */
  formatPost(post) {
    const featuredImage = post._embedded?.['wp:featuredmedia']?.[0];
    const author = post._embedded?.author?.[0];

    return {
      id: post.id,
      title: this.stripHtml(post.title?.rendered || post.title || 'Untitled'),
      content: post.content?.rendered || post.content || '',
      excerpt: this.cleanExcerpt(post.excerpt?.rendered || post.excerpt || ''),
      slug: post.slug || '',
      date: post.date || new Date().toISOString(),
      modified: post.modified || post.date || new Date().toISOString(),
      author: {
        id: author?.id || post.author || 1,
        name: author?.name || 'Belvoir Castle',
        avatar: author?.avatar_urls?.['96'] || null,
      },
      featuredImage: {
        id: featuredImage?.id || null,
        url: featuredImage?.source_url || null,
        alt: featuredImage?.alt_text || this.stripHtml(post.title?.rendered || post.title || 'Blog post image'),
        caption: featuredImage?.caption?.rendered || null,
      },
      categories: post.categories || [],
      tags: post.tags || [],
      link: post.link || '#',
      status: post.status || 'publish',
      sticky: post.sticky || false,
      meta: post.meta || {},
    };
  }

  /**
   * Strip HTML tags from text
   * @param {string} html - HTML string
   * @returns {string} Plain text
   */
  stripHtml(html) {
    if (!html) return '';
    const text = html.replace(/<[^>]*>/g, '');
    if (typeof document === 'undefined') return text.trim();
    const decoder = document.createElement('textarea');
    decoder.innerHTML = text;
    return decoder.value.trim();
  }

  /**
   * Clean excerpt text by removing WordPress truncation symbols and HTML
   * @param {string} excerpt - Raw excerpt text
   * @returns {string} Clean excerpt
   */
  cleanExcerpt(excerpt) {
    if (!excerpt) return '';
    
    // First strip HTML tags
    let cleanText = this.stripHtml(excerpt);
    
    // Remove WordPress ellipsis symbols and variations
    cleanText = cleanText
      .replace(/\[&hellip;\]/g, '') // WordPress ellipsis
      .replace(/\[…\]/g, '') // Unicode ellipsis
      .replace(/\[\.\.\.\]/g, '') // Three dots
      .replace(/\[&hellip\]/g, '') // Alternative hellip
      .replace(/\[&amp;hellip;\]/g, '') // Encoded hellip
      .replace(/\[&amp;hellip\]/g, '') // Alternative encoded hellip
      .replace(/…/g, '') // Unicode ellipsis character
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim();
    
    return cleanText;
  }

  /**
   * Format date for display
   * @param {string} dateString - ISO date string
   * @param {Object} options - Date formatting options
   * @returns {string} Formatted date
   */
  formatDate(dateString, options = {}) {
    const date = new Date(dateString);
    const formatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      ...options
    };
    return date.toLocaleDateString('en-GB', formatOptions);
  }

  /**
   * Generate excerpt from content
   * @param {string} content - HTML content
   * @param {number} length - Maximum length
   * @returns {string} Excerpt
   */
  generateExcerpt(content, length = 160) {
    const plainText = this.stripHtml(content);
    if (plainText.length <= length) return plainText;
    return plainText.substring(0, length).trim() + '...';
  }
}

export default new BlogService();